import cn from 'classnames'
import React, {ButtonHTMLAttributes, HTMLAttributeAnchorTarget, useContext} from 'react'
import {Link} from 'react-router-dom'
import {PageColorContext} from '@src/Features/PageColorContext.tsx'
import {ColorNames} from '@UI/theme/generated/ColorNames.ts'
import styles from './Button.module.less'

export enum ButtonType {
    primary = 'primary',
    secondary = 'secondary',
    third = 'third',
    text = 'text',
    link = 'link',
    clickableIcon = 'clickableIcon'
}

interface Props extends React.PropsWithChildren {
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
    disabled?: boolean
    icon?: React.ReactNode
    type?: ButtonType
    color?: ColorNames
    stopPropagation?: boolean
    minWidth?: number
    HTTLType?: ButtonHTMLAttributes<HTMLButtonElement>['type']
    rightIcon?: React.ReactNode
    className?: string
    onBlur?: ButtonHTMLAttributes<HTMLButtonElement>['onBlur']
    onFocus?: ButtonHTMLAttributes<HTMLButtonElement>['onFocus']
    pageColor?: boolean
    href?: string
    target?: HTMLAttributeAnchorTarget
    title?: string
    externalLink?: boolean
    style?: React.CSSProperties
}

export const Button = React.forwardRef<HTMLButtonElement, Props>(
    (
        {
            color = ColorNames.CORAL_MOSS,
            icon,
            rightIcon,
            onClick,
            disabled,
            children,
            type = ButtonType.primary,
            stopPropagation,
            minWidth,
            HTTLType = 'button',
            className,
            onBlur,
            onFocus,
            pageColor,
            href,
            target,
            title,
            externalLink = false,
            style
        },
        ref
    ) => {
        const pageColorName = useContext(PageColorContext)
        const button = (
            <button
                tabIndex={href ? -1 : undefined}
                title={href ? undefined : title}
                ref={ref}
                onBlur={onBlur}
                type={HTTLType}
                style={{minWidth, ...style}}
                onFocus={onFocus}
                className={cn(
                    styles.root,
                    styles[type],
                    styles[pageColor ? pageColorName : color],
                    href ? undefined : className,
                    {
                        [styles.disabled]: disabled
                    }
                )}
                onClick={e => {
                    if (disabled) return
                    if (stopPropagation && !href) {
                        e.preventDefault()
                        e.stopPropagation()
                    }
                    onClick && onClick(e)
                }}
                disabled={disabled}
            >
                {Boolean(icon) && <div className={styles.icon}>{icon}</div>}
                {Boolean(children) && <div className={styles.textContent}>{children}</div>}
                {Boolean(rightIcon) && <div className={styles.icon}>{rightIcon}</div>}
            </button>
        )
        if (href && !disabled && !externalLink)
            return (
                <Link
                    title={title}
                    target={target}
                    onClick={stopPropagation ? e => e.stopPropagation() : undefined}
                    className={cn(styles.linkWrapper, className)}
                    to={href}
                >
                    {button}
                </Link>
            )
        else if (href && !disabled && externalLink) {
            return (
                <a href={href} target="_blank" rel="noopener noreferrer"
                   className={cn(styles.linkWrapper, className)}>
                    {button}
                </a>
            )
        }
        return button

    }
)
